import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hotelsAPI, activitiesAPI } from '../services/api';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import { useSelectedActivities } from './SelectedActivitiesContext';

const HotelSearchPage = () => {
  const [searchParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { selectedActivities, toggleActivity, setSelectedActivities, isActivitySelected } = useSelectedActivities();
  
  const [searchFilters] = useState({
    destination: searchParams.get('destination') || '',
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: parseInt(searchParams.get('guests')) || 2,
    rooms: parseInt(searchParams.get('rooms')) || 1
  });

  // Hotel filters
  const [hotelFilters, setHotelFilters] = useState({
    propertyType: 'all',
    priceRange: [0, 50000],
    rating: 0,
    sortBy: 'price'
  });

  useEffect(() => {
    fetchData();
    // Get selected activities from URL params
    const activitiesParam = searchParams.get('activities');
    if (activitiesParam) {
      setSelectedActivities(activitiesParam.split(','));
    }
  }, []);

  const fetchData = async () => {
    try {
      setError('');
      const [activitiesResponse, hotelsResponse] = await Promise.all([
        activitiesAPI.getAll(),
        hotelsAPI.getAll()
      ]);
      
      // Handle activities data - ensure it's an array
      const activitiesData = Array.isArray(activitiesResponse.data) 
        ? activitiesResponse.data 
        : (activitiesResponse.data?.activities || []);
      
      console.log('Activities fetched:', activitiesData.length, activitiesData);
      setActivities(activitiesData);
      setHotels(hotelsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Unable to load data. Please check your connection and try again.');
      setActivities([]);
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityToggle = (activityId) => toggleActivity(activityId);

  const handleSelectAllActivities = () => {
    if (selectedActivities.length === activities.length) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities(activities.map(activity => activity._id));
    }
  };

  const filteredHotels = hotels.filter(hotel => {
    if (hotelFilters.propertyType !== 'all' && hotel.category !== hotelFilters.propertyType) {
      return false;
    }
    if (hotel.currentPrice < hotelFilters.priceRange[0] || hotel.currentPrice > hotelFilters.priceRange[1]) {
      return false;
    }
    if (hotel.rating < hotelFilters.rating) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    switch (hotelFilters.sortBy) {
      case 'price':
        return a.currentPrice - b.currentPrice;
      case 'rating':
        return b.rating - a.rating;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star" style={{color: '#FFD700'}}></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt" style={{color: '#FFD700'}}></i>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star" style={{color: '#FFD700'}}></i>);
    }

    return stars;
  };

  if (loading) {
    return (
      <section className="section" id="hotel-search">
        <div className="container">
          <LoadingSpinner text="Loading hotels and activities..." />
        </div>
      </section>
    );
  }

  return (
    <section className="section" id="hotel-search">
      <div className="container">
        {/* Search Summary */}
        <div className="search-summary">
          <h2>Hotels in {searchFilters.destination || 'Goa'}</h2>
          <div className="search-details">
            <span>{searchFilters.checkIn} to {searchFilters.checkOut}</span>
            <span>{searchFilters.guests} guests, {searchFilters.rooms} room(s)</span>
          </div>
        </div>

        {error && (
          <ErrorMessage 
            message={error} 
            type="error" 
            onClose={() => setError('')}
          />
        )}

        <div className="hotel-search-layout">
          {/* Left Sidebar - Activities */}
          <div className="activities-sidebar">
            <div className="sidebar-header">
              <h3>Available Activities</h3>
              <button 
                className="select-all-btn"
                onClick={handleSelectAllActivities}
              >
                {selectedActivities.length === activities.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="activities-list">
              {activities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  <p>No activities available at the moment.</p>
                </div>
              ) : (
                <>
                  {/* Selected Activities at the Top */}
                  {selectedActivities.length > 0 && (
                    <>
                      {selectedActivities.map(activityId => {
                        const activity = activities.find(a => String(a._id) === String(activityId));
                        if (!activity) return null;
                        return (
                          <div key={`selected-${activityId}`} className="activity-item activity-item-selected">
                            <div className="activity-item-content">
                              <input
                                type="checkbox"
                                id={`activity-${activityId}`}
                                checked={true}
                                onChange={() => handleActivityToggle(activityId)}
                                className="activity-checkbox-input"
                              />
                              <label htmlFor={`activity-${activityId}`} className="activity-item-label">
                                <span className="activity-title">{activity.activityName}</span>
                                <span className="activity-price">₹{activity.costing}</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                      {/* Separator */}
                      <div className="activity-separator"></div>
                    </>
                  )}
                  
                  {/* Unselected Activities */}
                  {activities
                    .filter(activity => !isActivitySelected(activity._id))
                    .map((activity) => (
                      <div key={activity._id} className="activity-item">
                        <div className="activity-item-content">
                          <input
                            type="checkbox"
                            id={`activity-${activity._id}`}
                            checked={isActivitySelected(activity._id)}
                            onChange={() => handleActivityToggle(activity._id)}
                            className="activity-checkbox-input"
                          />
                          <label htmlFor={`activity-${activity._id}`} className="activity-item-label">
                            <span className="activity-title">{activity.activityName}</span>
                            <span className="activity-price">₹{activity.costing}</span>
                          </label>
                        </div>
                      </div>
                    ))
                  }
                </>
              )}
            </div>
          </div>

          {/* Right Side - Hotels */}
          <div className="hotels-main">
            {/* Hotel Filters */}
            <div className="hotel-filters">
              <div className="filter-group">
                <label>Property Type</label>
                <select
                  value={hotelFilters.propertyType}
                  onChange={(e) => setHotelFilters({...hotelFilters, propertyType: e.target.value})}
                >
                  <option value="all">All Properties</option>
                  <option value="hotel">Hotels</option>
                  <option value="villa">Villas</option>
                  <option value="homestay">Homestays</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Price Range</label>
                <input
                  type="range"
                  min="0"
                  max="50000"
                  step="1000"
                  value={hotelFilters.priceRange[1]}
                  onChange={(e) => setHotelFilters({...hotelFilters, priceRange: [0, parseInt(e.target.value)]})}
                />
                <span>₹0 - ₹{hotelFilters.priceRange[1].toLocaleString()}</span>
              </div>
              
              <div className="filter-group">
                <label>Minimum Rating</label>
                <select
                  value={hotelFilters.rating}
                  onChange={(e) => setHotelFilters({...hotelFilters, rating: parseInt(e.target.value)})}
                >
                  <option value={0}>Any Rating</option>
                  <option value={3}>3+ Stars</option>
                  <option value={4}>4+ Stars</option>
                  <option value={4.5}>4.5+ Stars</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Sort By</label>
                <select
                  value={hotelFilters.sortBy}
                  onChange={(e) => setHotelFilters({...hotelFilters, sortBy: e.target.value})}
                >
                  <option value="price">Price (Low to High)</option>
                  <option value="rating">Rating (High to Low)</option>
                  <option value="name">Name (A to Z)</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="results-header">
              <h3>{filteredHotels.length} Properties Found</h3>
            </div>

            {/* Hotels Grid */}
            <div className="hotels-grid-small">
              {filteredHotels.map((hotel) => (
                <div key={hotel._id} className="hotel-card-small">
                  <Link to={`/hotel/${hotel._id}`} className="hotel-link">
                    <div className="hotel-image-small">
                      <img src={hotel.images[0]} alt={hotel.name} />
                      <div className="hotel-rating-small">
                        <div className="stars">
                          {renderStars(hotel.rating)}
                        </div>
                        <span className="rating-text">{hotel.ratingText}</span>
                      </div>
                    </div>
                    <div className="hotel-content-small">
                      <h4 className="hotel-name">{hotel.name}</h4>
                      <p className="hotel-location">{hotel.location}</p>
                      <p className="hotel-location-detail">{hotel.locationDetail}</p>
                      <div className="hotel-tags">
                        {hotel.tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                      <div className="hotel-amenities-small">
                        {hotel.amenities.slice(0, 2).map((amenity, index) => (
                          <div key={index} className="amenity-item">
                            <span className="amenity-icon">✓</span>
                            <span>{amenity}</span>
                          </div>
                        ))}
                      </div>
                      <div className="hotel-pricing-small">
                        <div className="price-row">
                          <span className="original-price">₹{hotel.originalPrice.toLocaleString()}</span>
                          <span className="current-price">₹{hotel.currentPrice.toLocaleString()}</span>
                        </div>
                        <div className="offer-badge">{hotel.offer}</div>
                        <div className="per-night">per night</div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HotelSearchPage;
